import type { Move } from '../move/move';
import {
  isMultiAttackMove,
  isOtoMove,
  isRevelationDanceMove,
  isTechnoBlastMove,
  isTerabrastMove,
  isWavesOfTheEarthMove,
  isWeatherBallMove,
} from '../move/move';
import { ARCEUS_SPECIES_ID } from '../pokemon/arceus';
import { getPlateType } from '../pokemon/PlateUtils';
import type { PokemonV2 } from '../types/pokemon';
import type { otherSetting } from '../types/other-setting';
import {
  BATTLE_FIELD_EREKI,
  BATTLE_FIELD_GLASS,
  BATTLE_FIELD_MISUTO,
  BATTLE_FIELD_PSYCHO_SAIKO,
  WEATHER_AME,
  WEATHER_HARE,
  WEATHER_OOAME,
  WEATHER_OOHIDERI,
  WEATHER_SUNA,
  WEATHER_YUKI,
} from '../types/other-setting';
import {
  POKEMON_TYPE_ELECTRIC,
  POKEMON_TYPE_FAIRY,
  POKEMON_TYPE_FIRE,
  POKEMON_TYPE_FLYING,
  POKEMON_TYPE_GRASS,
  POKEMON_TYPE_ICE,
  POKEMON_TYPE_NORMAL,
  POKEMON_TYPE_PSYCHIC,
  POKEMON_TYPE_ROCK,
  POKEMON_TYPE_WATER,
} from '../types/type';
import {
  hasWeatherBlockAbility,
  isErekiSkin,
  isFailySkin,
  isFreezeSkin,
  isNormalSkin,
  isSkySkin,
  isUruoiVoice,
} from './ability';
import { resolveMultiAttackMoveType } from './multi-attack';
import { resolveRevelationDanceMoveType } from './revelation-dance';
import { resolveTechnoBlastMoveType } from './techno-blast';

/**
 * 攻撃側の要素で技タイプが変化するものを解決して、最終的な技タイプIDを返す。
 *
 * - UI上の「充電(じゅうでん)」表示など、技タイプ依存の追加設定表示に使用する想定。
 * - 計算ロジック側の型変化順序（特性→テラバースト→専用技→天候/フィールド→プレート→ノーマルスキン）に合わせる。
 */
export const resolveOffenceMoveType = (
  move: Move,
  offencePokemon: PokemonV2,
  otherSetting?: otherSetting,
  deffencePokemon?: PokemonV2,
): number => {
  // 攻撃モードではテラスタイプ補正の扱いが異なるが、タイプ自体の解決は共通なのでここでは分岐しない
  let moveType = move.type;

  // めざめるダンスは使用者のタイプ（タイプ1/テラスタル）で攻撃する（ステラはタイプが変わらない）
  if (isRevelationDanceMove(move)) {
    moveType = resolveRevelationDanceMoveType(offencePokemon);
  }

  // テクノバスターはカセットに応じてタイプが変化する
  if (isTechnoBlastMove(move)) {
    const technoBlastType = resolveTechnoBlastMoveType(offencePokemon);
    if (technoBlastType) {
      moveType = technoBlastType;
    }
  }

  // マルチアタックはメモリに応じてタイプが変化する
  if (isMultiAttackMove(move)) {
    const multiAttackType = resolveMultiAttackMoveType(offencePokemon);
    if (multiAttackType) {
      moveType = multiAttackType;
    }
  }

  // うるおいボイス特性：音技を水タイプに変更
  if (isUruoiVoice(offencePokemon) && isOtoMove(move)) {
    moveType = POKEMON_TYPE_WATER;
  } else if (isFailySkin(offencePokemon) && moveType === POKEMON_TYPE_NORMAL) {
    moveType = POKEMON_TYPE_FAIRY;
  } else if (isSkySkin(offencePokemon) && moveType === POKEMON_TYPE_NORMAL) {
    moveType = POKEMON_TYPE_FLYING;
  } else if (isErekiSkin(offencePokemon) && moveType === POKEMON_TYPE_NORMAL) {
    moveType = POKEMON_TYPE_ELECTRIC;
  } else if (isFreezeSkin(offencePokemon) && moveType === POKEMON_TYPE_NORMAL) {
    moveType = POKEMON_TYPE_ICE;
  }

  // テラバーストのタイプ変更処理（テラスタル時のみ）
  if (isTerabrastMove(move) && offencePokemon.terasu_type) {
    moveType = offencePokemon.terasu_type;
  }

  // ウェザーボール使用時の天気対応タイプ変更
  if (isWeatherBallMove(move) && otherSetting) {
    // エアロック・ノーてんき特性チェック（天気無効化）
    const weatherBlocked = hasWeatherBlockAbility(offencePokemon) || hasWeatherBlockAbility(deffencePokemon);

    if (!weatherBlocked) {
      if (otherSetting.weather === WEATHER_HARE || otherSetting.weather === WEATHER_OOHIDERI) {
        moveType = POKEMON_TYPE_FIRE;
      } else if (otherSetting.weather === WEATHER_AME || otherSetting.weather === WEATHER_OOAME) {
        moveType = POKEMON_TYPE_WATER;
      } else if (otherSetting.weather === WEATHER_YUKI) {
        moveType = POKEMON_TYPE_ICE;
      } else if (otherSetting.weather === WEATHER_SUNA) {
        moveType = POKEMON_TYPE_ROCK;
      }
    }
  }

  // だいちのはどう使用時のフィールド対応タイプ変更
  if (isWavesOfTheEarthMove(move) && otherSetting) {
    if (otherSetting.battle_field === BATTLE_FIELD_GLASS) {
      moveType = POKEMON_TYPE_GRASS;
    } else if (otherSetting.battle_field === BATTLE_FIELD_MISUTO) {
      moveType = POKEMON_TYPE_FAIRY;
    } else if (otherSetting.battle_field === BATTLE_FIELD_PSYCHO_SAIKO) {
      moveType = POKEMON_TYPE_PSYCHIC;
    } else if (otherSetting.battle_field === BATTLE_FIELD_EREKI) {
      moveType = POKEMON_TYPE_ELECTRIC;
    }
  }

  const normalizedSpeciesId = offencePokemon.species_id != null ? Number(offencePokemon.species_id) : undefined;
  const isArceus = normalizedSpeciesId === ARCEUS_SPECIES_ID || offencePokemon.id === ARCEUS_SPECIES_ID;

  // さばきのつぶて（アルセウス）はプレートに応じてタイプが変化する
  if (move.id === 449 && isArceus) {
    const plateType = getPlateType(offencePokemon.selected_item_id);
    if (plateType !== null) {
      moveType = plateType;
    }
  }

  // ノーマルスキンは使用する技を全てノーマルタイプにする
  if (isNormalSkin(offencePokemon)) {
    moveType = POKEMON_TYPE_NORMAL;
  }

  return moveType;
};

